import React from "react";
import Panel from "../../../common/components/panel";
import {
  ResourceMetadataFieldFragment,
  RmFieldLookupValueFragment,
  RmLookupAutoMap,
  RmLookupTwoSourcePriorityMappingTypeFragment,
  RmMappingDetailsFragment,
  RmProblemConnectionFragment,
  useResourceMetadataForFieldQuery,
  useRmMappingMoveLookupTwoSourcePriorityItemAfterMutation,
  useRmMappingSetLookupTwoSourceTransformMutation,
  useRmMappingSetPrimarySourceMutation,
  useRmMappingSetSecondarySourceMutation,
  useRmMappingUpdateLookupBasicMutation,
  useRmProblemAcknowledgeMutation,
} from "../../../graphql/generated";
import { NotificationContext } from "../../../common/context/notification";
import {
  LookupValueSelector,
  SelectedLookupValue,
  selectedValueSpread,
} from "./LookupValueSelector";
import LoadingIcon from "../../../common/components/loadingicon";
import { diceCoefficient } from "dice-coefficient";
import ProblemStatus from "../problem-status";
import {
  ResourceMetadataLookupValueComponent,
  resourceMetadataLookupValueForValue,
} from "../resource-metadata/resource-metadata-lookup-value-component";
import { ResourceMetadataFieldComponent } from "../resource-metadata/resource-metadata-field-component";
import { EditableSource, sourceName } from "../mapping-source";
import DescriptionListContainer from "../../../common/components/descriptionlistcontainer";
import { DescriptionList } from "../../../common/components/descriptionlist";
import { Link } from "react-router-dom";
import {
  DragDropContext,
  Draggable,
  DraggingStyle,
  Droppable,
  DropResult,
  NotDraggingStyle,
} from "react-beautiful-dnd";
import { classNames } from "../../../common/utils/classnames";
import CommonSwitch from "../../../common/components/switch";

const RMLookupTwoSourcePriorityMappingTypeControl: React.FC<{
  mapping: RmMappingDetailsFragment;
  mappingType: RmLookupTwoSourcePriorityMappingTypeFragment;
  problems?: RmProblemConnectionFragment;
}> = ({ mapping, mappingType, problems }) => {
  const { notifier } = React.useContext(NotificationContext);
  const [primarySource, setPrimarySource] = React.useState<string>("");
  const [secondarySource, setSecondarySource] = React.useState<string>();
  const [destination, setDestination] = React.useState<SelectedLookupValue>({
    state: "ignore",
    newValue: "",
  });
  const [suggestionSetIndex, setSuggestionSetIndex] = React.useState(-1);

  const options = mapping.destinationIsLocalLookup
    ? mapping.field.localLookupValues
    : mapping.field.standardLookupValues;

  const [{ fetching: setPrimarySourceFetching }, setPrimarySourceMutation] =
    useRmMappingSetPrimarySourceMutation();
  const [{ fetching: setSecondarySourceFetching }, setSecondarySourceMutation] =
    useRmMappingSetSecondarySourceMutation();
  const [{ fetching: updateFetching }, updateLookupBasicMutation] =
    useRmMappingUpdateLookupBasicMutation();

  const [{ fetching }, mutation] =
    useRmMappingSetLookupTwoSourceTransformMutation();
  const [{ data: resourceMetadataPrimary }] = useResourceMetadataForFieldQuery({
    variables: {
      id: mapping.field.rootResource.resourceMetadataId,
      fieldName:
        mappingType.primarySource.__typename === "RMFieldMappingSource"
          ? mappingType.primarySource.name
          : "",
    },
    pause: mappingType.primarySource.__typename !== "RMFieldMappingSource",
  });
  const [{ data: resourceMetadataSecondary }] =
    useResourceMetadataForFieldQuery({
      variables: {
        id: mapping.field.rootResource.resourceMetadataId,
        fieldName:
          mappingType.secondarySource.__typename === "RMFieldMappingSource"
            ? mappingType.secondarySource.name
            : "",
      },
      pause: mappingType.primarySource.__typename !== "RMFieldMappingSource",
    });

  const resourceMetadataPrimaryField =
    resourceMetadataPrimary?.resourceMetadata.field ?? undefined;
  const resourceMetadataSecondaryField =
    resourceMetadataSecondary?.resourceMetadata.field ?? undefined;

  const setLookup = React.useCallback(async () => {
    if (fetching) {
      return;
    }
    await mutation({
      mappingId: mapping.id,
      primarySource,
      secondarySource,
      ...selectedValueSpread(destination),
    }).then(notifier.notifyGraphql("Mapping updated"));
  }, [
    fetching,
    mutation,
    mapping,
    primarySource,
    secondarySource,
    destination,
  ]);

  const onSplitOnCommaToggleChanged = React.useCallback(async () => {
    if (updateFetching) {
      return;
    }
    await updateLookupBasicMutation({
      mappingId: mapping.id,
      splitOnComma: !mappingType.splitOnComma,
      splitOnCommaInArray: mappingType.splitOnCommaInArray ?? false,
      autoMap: RmLookupAutoMap.None, // This doesn't apply to TwoSource mappings, but we use the same mutation for both
    }).then(notifier.notifyGraphql("Split on commas toggled"));
  }, [
    mapping.id,
    mappingType.splitOnComma,
    mappingType.splitOnCommaInArray,
    updateFetching,
    updateLookupBasicMutation,
  ]);

  const [{ fetching: moveItemFetching }, moveItemMutation] =
    useRmMappingMoveLookupTwoSourcePriorityItemAfterMutation();

  const anyMutationIsFetching = moveItemFetching || updateFetching || fetching;

  const onDragEnd = React.useCallback(
    async (result: DropResult) => {
      // Current update happening
      if (moveItemFetching) {
        return;
      }

      // dropped outside the list
      if (!result.destination) {
        return;
      }

      // Moved to the same place
      if (result.source.index == result.destination.index) {
        return;
      }

      let destinationIndex = result.destination.index;
      if (result.source.index < result.destination.index) {
        destinationIndex += 1;
      }

      let item;
      {
        const transform = mappingType.transforms[result.source.index];
        item = {
          primary: transform.primaryFrom,
          secondary: transform.secondaryFrom,
        };
      }

      let after;
      if (destinationIndex === 0) {
        after = null;
      } else {
        const transform = mappingType.transforms[destinationIndex - 1];
        after = {
          primary: transform.primaryFrom,
          secondary: transform.secondaryFrom,
        };
      }

      await moveItemMutation({
        mappingId: mapping.id,
        item,
        after,
      }).then(notifier.notifyGraphql("Moved"));
    },
    [moveItemFetching, moveItemMutation, mapping, mappingType],
  );

  function getTrStyle(
    isDragging: boolean,
    style: DraggingStyle | NotDraggingStyle | undefined,
  ): React.CSSProperties {
    if (isDragging) {
      return {
        display: "table",
        background: "rgba(255, 255, 255, 0.8)",
        ...style,
      };
    } else {
      return {
        ...style,
      };
    }
  }

  const filteredProblems = React.useMemo(
    () =>
      (problems?.edges ?? []).filter(
        (edge) =>
          edge.node.data["problem_type"] === "unidentified_two_source_lookup" ||
          edge.node.data["problem_type"] === "local_data_not_configured",
      ),
    [problems?.edges],
  );

  return (
    <>
      <Panel>
        <Panel.Title>Two-source Lookup Priority Mapping</Panel.Title>
        <Panel.Body>
          <DescriptionList>
            <DescriptionListContainer title="Primary source">
              <EditableSource
                source={mappingType.primarySource}
                onSubmit={(_, sourceInput) =>
                  setPrimarySourceMutation({
                    mappingId: mapping.id,
                    source: sourceInput,
                  })
                }
                isSubmitting={setPrimarySourceFetching}
              />
            </DescriptionListContainer>
            <DescriptionListContainer title="Secondary source">
              <EditableSource
                source={mappingType.secondarySource}
                onSubmit={(_, sourceInput) =>
                  setSecondarySourceMutation({
                    mappingId: mapping.id,
                    source: sourceInput,
                  })
                }
                isSubmitting={setSecondarySourceFetching}
              />
            </DescriptionListContainer>
            <DescriptionListContainer
              title="Split on commas"
              info="Whether or not to treat a comma-separated string as multiple values. Enable this for RETS MLSes and certain API MLSes that return multiple values in a single comma-separated string."
            >
              <CommonSwitch
                label="Split on commas"
                enabled={mappingType.splitOnComma ?? false}
                toggle={onSplitOnCommaToggleChanged}
              />
            </DescriptionListContainer>
            <DescriptionListContainer title="Transforms" span={4}>
              <table
                className={classNames(
                  "w-full text-sm text-left",
                  anyMutationIsFetching ? "opacity-25" : "",
                )}
              >
                <thead>
                  <tr>
                    <th className="p-1">
                      Source:{" "}
                      <ResourceMetadataFieldComponent
                        source={mappingType.primarySource}
                        field={resourceMetadataPrimaryField}
                      />
                    </th>
                    <th className="p-1">
                      Source:{" "}
                      <ResourceMetadataFieldComponent
                        source={mappingType.secondarySource}
                        field={resourceMetadataSecondaryField}
                      />
                    </th>
                    <th className="p-1">Destination: {mapping.field.name}</th>
                  </tr>
                </thead>

                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="bruh">
                    {(provided) => (
                      <tbody ref={provided.innerRef}>
                        {mappingType.transforms.map((transform, idx) => (
                          <Draggable
                            key={`${transform.primaryFrom}-${transform.secondaryFrom}`}
                            draggableId={`${transform.primaryFrom}-${transform.secondaryFrom}`}
                            index={idx}
                            isDragDisabled={moveItemFetching}
                          >
                            {(provided, snapshot) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={getTrStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style,
                                )}
                                className="border-t"
                              >
                                <td className="p-1">
                                  ⣿{" "}
                                  <ResourceMetadataLookupValueComponent
                                    field={resourceMetadataPrimaryField}
                                    value={transform.primaryFrom}
                                  />
                                </td>
                                <td className="p-1">
                                  {transform.secondaryFrom && (
                                    <ResourceMetadataLookupValueComponent
                                      field={resourceMetadataSecondaryField}
                                      value={transform.secondaryFrom}
                                    />
                                  )}
                                  {!transform.secondaryFrom && (
                                    <span className="text-gray-400">null</span>
                                  )}
                                </td>
                                <td className="p-1">
                                  {transform.to && transform.to}
                                  {!transform.to && (
                                    <span className="text-gray-400">
                                      ignore
                                    </span>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </DragDropContext>
                <tbody>
                  <tr key="add" className="border-t">
                    <td className="p-1">
                      <input
                        type="text"
                        value={primarySource}
                        onChange={(e) => setPrimarySource(e.target.value)}
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        value={secondarySource}
                        onChange={(e) => setSecondarySource(e.target.value)}
                      />
                    </td>
                    <td className="p-1">
                      <LookupValueSelector
                        selected={destination}
                        setSelected={setDestination}
                        options={options}
                        creationIsAllowed={mapping.destinationIsLocalLookup}
                      />{" "}
                      {!fetching && (
                        <button onClick={() => setLookup()}>Set</button>
                      )}
                      {fetching && <LoadingIcon />}
                    </td>
                  </tr>
                </tbody>
              </table>
            </DescriptionListContainer>
          </DescriptionList>
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Title>Suggestions</Panel.Title>
        <Panel.Body>
          <table className="w-full text-sm text-left">
            <thead>
              <tr>
                <th className="p-1">
                  Source: {sourceName(mappingType.primarySource)}
                </th>
                <th className="p-1">
                  Source: {sourceName(mappingType.secondarySource)}
                </th>
                <th className="p-1">Destination: {mapping.field.name}</th>
                <th className="p-1">
                  {filteredProblems.length && (
                    <button
                      className="bg-zenlist-500 hover:bg-zenlist-700 text-white font-bold py-2 px-4 rounded"
                      onClick={() => setSuggestionSetIndex(0)}
                    >
                      Set all
                    </button>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredProblems.map((edge, idx) => (
                <Suggestion
                  key={idx}
                  edge={edge}
                  mapping={mapping}
                  primaryField={resourceMetadataPrimaryField}
                  secondaryField={resourceMetadataSecondaryField}
                  options={options}
                  shouldTriggerSet={suggestionSetIndex === idx}
                  setSuggestionSetIndex={setSuggestionSetIndex}
                  isLast={idx === filteredProblems.length - 1}
                />
              ))}
            </tbody>
          </table>
        </Panel.Body>
      </Panel>
    </>
  );
};

const Suggestion: React.FC<{
  edge: RmProblemConnectionFragment["edges"][number];
  mapping: RmMappingDetailsFragment;
  primaryField?: ResourceMetadataFieldFragment;
  secondaryField?: ResourceMetadataFieldFragment;
  options: RmFieldLookupValueFragment[];
  isLast: boolean;
  setSuggestionSetIndex: React.Dispatch<React.SetStateAction<number>>;
  shouldTriggerSet: boolean;
}> = ({
  edge,
  mapping,
  primaryField,
  secondaryField,
  options,
  isLast,
  setSuggestionSetIndex,
  shouldTriggerSet,
}) => {
    if (edge.node.data["problem_type"] === "unidentified_two_source_lookup") {
      return (
        <UnidentifiedLookupSuggestion
          edge={edge}
          mapping={mapping}
          primaryField={primaryField}
          secondaryField={secondaryField}
          options={options}
          isLast={isLast}
          setSuggestionSetIndex={setSuggestionSetIndex}
          shouldTriggerSet={shouldTriggerSet}
        />
      );
    }
    if (edge.node.data["problem_type"] === "local_data_not_configured") {
      return (
        <LocalDataNotConfiguredSuggestion
          edge={edge}
          mapping={mapping}
          isLast={isLast}
          setSuggestionSetIndex={setSuggestionSetIndex}
          shouldTriggerSet={shouldTriggerSet}
        />
      );
    }

    return <></>;
  };

const UnidentifiedLookupSuggestion: React.FC<{
  edge: RmProblemConnectionFragment["edges"][number];
  mapping: RmMappingDetailsFragment;
  primaryField?: ResourceMetadataFieldFragment;
  secondaryField?: ResourceMetadataFieldFragment;
  options: RmFieldLookupValueFragment[];
  isLast: boolean;
  setSuggestionSetIndex: React.Dispatch<React.SetStateAction<number>>;
  shouldTriggerSet: boolean;
}> = ({
  edge,
  mapping,
  primaryField,
  secondaryField,
  options,
  isLast,
  setSuggestionSetIndex,
  shouldTriggerSet,
}) => {
    const { notifier } = React.useContext(NotificationContext);

    const creationIsAllowed = mapping.destinationIsLocalLookup;
    const problem = edge.node;
    const primarySource = problem.data["primary_value"];
    const secondarySource = problem.data["secondary_value"];
    const secondaryDisplay = secondarySource ?? "(null)";
    const checkValues = [
      secondarySource ? `${primarySource} / ${secondarySource}` : primarySource,
    ];
    const isAcknowledged = problem.acknowledgedAt != null;

    const primaryLookupValue = resourceMetadataLookupValueForValue(
      primarySource,
      primaryField,
    );
    const secondaryLookupValue = secondarySource
      ? resourceMetadataLookupValueForValue(secondarySource, secondaryField)
      : null;

    if (primaryLookupValue && secondaryLookupValue) {
      checkValues.push(
        `${primaryLookupValue.name} / ${secondaryLookupValue.name}`,
      );
    } else if (primaryLookupValue) {
      checkValues.push(primaryLookupValue.name);
    } else if (secondaryLookupValue) {
      checkValues.push(secondaryLookupValue.name);
    }

    const suggestions = options
      .map((value) => {
        return {
          value: value.value,
          definition: value.definition,
          coefficient: checkValues
            .map((checkValue) => diceCoefficient(checkValue, value.value))
            .reduce((a, b) => Math.max(a, b)),
        };
      })
      .filter((suggestion) => suggestion.coefficient > 0.5);

    suggestions.sort((a, b) => b.coefficient - a.coefficient);
    suggestions.splice(3);

    const [destination, setDestination] = React.useState<SelectedLookupValue>(
      () => {
        if (suggestions.length && suggestions[0].coefficient > 0.8) {
          return {
            state: "existing",
            selectedValue: suggestions[0].value,
            newValue: "",
          };
        } else if (creationIsAllowed) {
          return {
            state: "new",
            newValue: "",
          };
        } else {
          return {
            state: "ignore",
            newValue: "",
          };
        }
      },
    );

    const [{ fetching }, mutation] =
      useRmMappingSetLookupTwoSourceTransformMutation();
    const [, mutationAcknowledge] = useRmProblemAcknowledgeMutation();

    const setLookup = React.useCallback(async () => {
      if (fetching) {
        return;
      }
      const { error } = await mutation({
        mappingId: mapping.id,
        primarySource,
        secondarySource,
        ...selectedValueSpread(destination),
      }).then(notifier.notifyGraphql("Mapping updated"));
      if (!error) {
        mutationAcknowledge({
          problemId: problem.id,
        });
      }
    }, [
      fetching,
      mutation,
      mutationAcknowledge,
      mapping,
      problem,
      primarySource,
      secondarySource,
      destination,
    ]);

    const setLookupFromSetAll = async () => {
      await setLookup();
      setSuggestionSetIndex((prevState) => (isLast ? -1 : prevState + 1));
    };

    React.useEffect(() => {
      if (shouldTriggerSet) setLookupFromSetAll();
    }, [shouldTriggerSet]);

    return (
      <tr>
        <td>
          <ResourceMetadataLookupValueComponent
            field={primaryField}
            value={primarySource}
          />
        </td>
        <td>
          <ResourceMetadataLookupValueComponent
            field={secondaryField}
            value={secondaryDisplay}
          />
        </td>
        <td>
          <LookupValueSelector
            selected={destination}
            setSelected={setDestination}
            suggestions={suggestions}
            options={options}
            disabled={isAcknowledged}
            creationIsAllowed={creationIsAllowed}
          />
          {!fetching && (
            <button onClick={() => setLookup()} disabled={isAcknowledged}>
              Set
            </button>
          )}
          {fetching && <LoadingIcon />}
        </td>

        <td>
          <ProblemStatus problem={problem} />
        </td>
      </tr>
    );
  };

const LocalDataNotConfiguredSuggestion: React.FC<{
  edge: RmProblemConnectionFragment["edges"][number];
  mapping: RmMappingDetailsFragment;
  isLast: boolean;
  setSuggestionSetIndex: React.Dispatch<React.SetStateAction<number>>;
  shouldTriggerSet: boolean;
}> = ({ edge, mapping, isLast, setSuggestionSetIndex, shouldTriggerSet }) => {
  const problem = edge.node;
  const fieldName = mapping.field.name;

  React.useEffect(() => {
    if (shouldTriggerSet)
      setSuggestionSetIndex((prevState) => (isLast ? -1 : prevState + 1));
  }, [shouldTriggerSet]);

  if (fieldName === "LocalPropertyType") {
    return (
      <tr>
        <td colSpan={2}>
          <strong>{problem.data["lookup_value"]}</strong> is configured on this
          field but is not configured as an MLS local property type.
        </td>
        <td>
          <Link
            to={`/resource_mapping/mlses/${problem.rootResource.mls.id}/property_types`}
            className="text-blue-900 hover:underline"
          >
            Configure property types
          </Link>
        </td>
        <td>
          <ProblemStatus problem={problem} />
        </td>
      </tr>
    );
  } else if (fieldName === "LocalStatus") {
    return (
      <tr>
        <td colSpan={2}>
          <strong>{problem.data["lookup_value"]}</strong> is configured on this
          field but is not configured as an MLS local status.
        </td>
        <td>
          <Link
            to={`/resource_mapping/mlses/${problem.rootResource.mls.id}/statuses`}
            className="text-blue-900 hover:underline"
          >
            Configure statuses
          </Link>
        </td>
        <td>
          <ProblemStatus problem={problem} />
        </td>
      </tr>
    );
  } else {
    return <></>;
  }
};

export default RMLookupTwoSourcePriorityMappingTypeControl;
